import { create } from 'zustand';

export type StudioTool = 'generate' | 'edit' | 'blend' | 'upscale' | 'video' | 'caption' | 'text' | 'upload';
export type PipelineRole = 'default' | 'startFrame' | 'endFrame' | 'styleRef' | 'assetRef' | 'blendInput' | 'editSource' | 'textSource';

export interface StudioAsset {
  id: string;
  type: 'image' | 'video';
  data: string; // base64 for images, URL for videos/signed URLs
  mimeType: string;
  prompt?: string;
  description?: string;
  tool: StudioTool;
  createdAt: number;
  // Server-side fields (when loaded from project)
  serverId?: string; // DB asset ID
  gcsPath?: string;
}

interface StudioState {
  // Project
  projectId: string | null;
  projectName: string | null;
  setProject: (id: string | null, name: string | null) => void;

  // Tool
  activeTool: StudioTool;
  setTool: (tool: StudioTool) => void;

  // Assets
  assets: StudioAsset[];
  activeAssetId: string | null;
  addAsset: (asset: Omit<StudioAsset, 'id' | 'createdAt'>) => string;
  setActiveAsset: (id: string | null) => void;
  removeAsset: (id: string) => void;
  setAssetServerId: (id: string, serverId: string) => void;
  clearAssets: () => void;
  loadAssets: (assets: StudioAsset[]) => void;

  // Pipeline flow
  sendToTool: (assetId: string, tool: StudioTool, role?: PipelineRole) => void;
  pipelineSource: StudioAsset | null;
  pipelineRole: PipelineRole;

  // Generation state
  isGenerating: boolean;
  setGenerating: (v: boolean) => void;

  // Video operation tracking
  pendingVideoOps: Record<string, string>;
  addVideoOp: (operationId: string, jobId: string) => void;
  removeVideoOp: (operationId: string) => void;

  // Dirty tracking (unsaved changes)
  isDirty: boolean;
  setDirty: (v: boolean) => void;
}

let _nextId = 1;
const makeId = () => `asset-${Date.now()}-${_nextId++}`;

const MAX_PERSISTED_ASSETS = 20;

export const useStudioStore = create<StudioState>()(
    (set, get) => ({
      projectId: null,
      projectName: null,
      setProject: (id, name) => set({ projectId: id, projectName: name }),

      activeTool: 'generate',
      setTool: (tool) => set({ activeTool: tool, pipelineSource: null, pipelineRole: 'default' }),

      assets: [],
      activeAssetId: null,

      addAsset: (partial) => {
        const id = makeId();
        const asset: StudioAsset = { ...partial, id, createdAt: Date.now() };
        set((s) => {
          const newAssets = [asset, ...s.assets].slice(0, MAX_PERSISTED_ASSETS);
          return { assets: newAssets, activeAssetId: id, isDirty: true };
        });
        return id;
      },

      setActiveAsset: (id) => set({ activeAssetId: id }),

      // The server saved it already: a project save must not upload it again.
      setAssetServerId: (id, serverId) =>
        set((s) => ({ assets: s.assets.map((a) => (a.id === id ? { ...a, serverId } : a)) })),

      removeAsset: (id) =>
        set((s) => {
          const remaining = s.assets.filter((a) => a.id !== id);
          return {
            assets: remaining,
            activeAssetId: s.activeAssetId === id ? (remaining[0]?.id ?? null) : s.activeAssetId,
            isDirty: true,
          };
        }),

      clearAssets: () => set({ assets: [], activeAssetId: null, isDirty: false }),

      loadAssets: (assets) =>
        set({
          assets,
          activeAssetId: assets[0]?.id ?? null,
          isDirty: false,
        }),

      sendToTool: (assetId, tool, role = 'default') => {
        const asset = get().assets.find((a) => a.id === assetId);
        if (asset) set({ activeTool: tool, pipelineSource: asset, pipelineRole: role });
      },

      pipelineSource: null,
      pipelineRole: 'default',

      isGenerating: false,
      setGenerating: (v) => set({ isGenerating: v }),

      pendingVideoOps: {},
      addVideoOp: (operationId, jobId) =>
        set((s) => ({
          pendingVideoOps: { ...s.pendingVideoOps, [operationId]: jobId },
        })),
      removeVideoOp: (operationId) =>
        set((s) => {
          const pendingVideoOps = { ...s.pendingVideoOps };
          delete pendingVideoOps[operationId];
          return { pendingVideoOps };
        }),

      isDirty: false,
      setDirty: (v) => set({ isDirty: v }),
    })
);
